/**
 * A person's own queue: views they saved, the one they start on, and the
 * colors they give rows (087).
 *
 * PERSONAL AND PER DESK. "Show Peter's tickets in pink" is one agent's way of
 * reading their queue, not the desk's; and the same person working two desks
 * has different queues, owners and types in each, so their views do not
 * carry across.
 *
 * Stored as one document rather than rows: it is read whole on every queue
 * load and written whole when it changes, by one person, so there is
 * nothing for a table of its own to make easier.
 */
import { z } from 'zod';

/** The colors a row can be given. Named, so the page draws each in both themes. */
export const ROW_COLORS = ['pink', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'grey'] as const;
export type RowColor = (typeof ROW_COLORS)[number];

/** What a color rule can look at on a row. */
export const ROW_COLOR_FIELDS = ['assignee', 'requester', 'queue', 'requestType', 'priority', 'tag'] as const;
export type RowColorField = (typeof ROW_COLOR_FIELDS)[number];

const id = z.string().trim().min(1).max(40);

export const savedViewSchema = z.object({
  id,
  name: z.string().trim().min(1).max(60),
  /** The tab: open, mine, unassigned, ... (queueViews.ts). */
  view: z.string().trim().min(1).max(30),
  mode: z.enum(['list', 'board']).optional(),
  sort: z.string().trim().max(30).optional(),
  queueKey: z.string().trim().max(60).optional(),
  requestType: z.string().trim().max(120).optional(),
  /** Somebody else's queue, by oid. */
  assigneeOid: z.string().trim().max(120).optional(),
  tag: z.string().trim().max(40).optional(),
  density: z.enum(['compact', 'normal']).optional(),
});
export type SavedView = z.infer<typeof savedViewSchema>;

export const rowColorRuleSchema = z.object({
  id,
  field: z.enum(ROW_COLOR_FIELDS),
  /** An oid, an email, a queue key, a type label, a priority or a tag. */
  value: z.string().trim().min(1).max(200),
  color: z.enum(ROW_COLORS),
});
export type RowColorRule = z.infer<typeof rowColorRuleSchema>;

export const MAX_SAVED_VIEWS = 20;
export const MAX_COLOR_RULES = 30;

export const queuePrefsSchema = z
  .object({
    views: z.array(savedViewSchema).max(MAX_SAVED_VIEWS).default([]),
    /** The view the queue opens on. Null: the desk's usual Open tab. */
    defaultViewId: id.nullable().default(null),
    /** In order: the first rule a row matches gives it its color. */
    colors: z.array(rowColorRuleSchema).max(MAX_COLOR_RULES).default([]),
  })
  // A default that names a view that is not there opens on nothing; drop it
  // rather than refuse the whole save over it.
  .transform((p) => ({
    ...p,
    defaultViewId: p.defaultViewId && p.views.some((v) => v.id === p.defaultViewId) ? p.defaultViewId : null,
  }));
export type QueuePrefs = z.infer<typeof queuePrefsSchema>;

export const EMPTY_QUEUE_PREFS: QueuePrefs = { views: [], defaultViewId: null, colors: [] };

/** The parts of a queue row a color rule can read. */
export interface ColorableRow {
  assignee_oid: string | null;
  requester_email: string | null;
  queue_key: string | null;
  request_type: string | null;
  priority: string;
  tags: readonly string[];
}

/**
 * The color a row gets: the FIRST rule it matches, so a person orders their
 * rules by what matters most to them. Null when none match.
 *
 * An address is compared without case, because the same person arrives as
 * Peter.Ross@ and peter.ross@ depending on who typed it.
 */
export function rowColorFor(row: ColorableRow, rules: readonly RowColorRule[]): RowColor | null {
  for (const r of rules) {
    const v = r.value.trim();
    const hit =
      r.field === 'assignee'
        ? row.assignee_oid === v
        : r.field === 'requester'
          ? (row.requester_email ?? '').trim().toLowerCase() === v.toLowerCase()
          : r.field === 'queue'
            ? row.queue_key === v
            : r.field === 'requestType'
              ? row.request_type === v
              : r.field === 'priority'
                ? row.priority === v
                : row.tags.includes(v);
    if (hit) return r.color;
  }
  return null;
}
